// Packages
import React, { Fragment, useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"

// Utils
import getValue from "object-getvalue"
import {
  isInternationalCheckoutEnabled,
  isNaSite,
  formatPreorderDate,
} from "lib/utils/helpers"

// import TC from 'constants/TrackingConstants';

// Config
import SwatchConfig from "components/Stacker/config/SwatchConfig"
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"

// Components
import ProductSizes from "components/Stacker/components/ProductSizes/ProductSizes"
import AddToBag from "components/Stacker/components/AddToBag/AddToBag"
import ModalConfig from "components/Stacker/config/ModalConfig"

// Actions
import {
  setSelectedGiftCardAmount,
  updateNotifyStatus,
} from "components/Stacker/actions/ProductDetailActions"
import { toggleModal } from "components/Stacker/actions/ModalActions"
import { selectQuickViewProduct } from "components/Stacker/actions/ProductDetailActions"

// import LoadingRopisMain from 'components/ropis/RopisMain/LoadingRopisMain';
import ProductSwatches from "components/Stacker/components/ProductSwatches/ProductSwatches"
import ProductMetals from "components/Stacker/components/ProductMetals/ProductMetals"
import CustomizeProductConfig from "components/Stacker/components/CustomizeProductConfig/CustomizeProductConfig"

import "./ProductOverview.scss"
import { getDY_SR } from "stackerConfig"
import { useDispatch } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

// this should hold the Product Details on PDP page.

function ProductOverview({
  cartMetaData: { isDataLoading, isError, message = "" },
  design,
  displayAddtoBag,
  enagraveableProductConfig,
  fontCode,
  isCsAgent,
  isEPS,
  isGiftCard,
  isPDPPage,
  isQuickView,
  enableRopis,
  motifCode,
  productDetails,
  productDetails: {
    variantOptions,
    isEngagementProduct,
    purchasable,
    isProductConfigurable,
    configurableProductType,
    url,
    isAlphabetStone,
  },
  productType = null,
  priceInventory = {},
  pid,
  refinerId = null,
  selectedSize,
  sizeSelectionError,
  selectedVariant,
  selectedVariant: { contactSpecialist = "false" } = {
    contactSpecialist: "false",
  },
  selectedVariantIndex,
  shopTheLookURL,
  text,
  metal,
  productMetals,
  icon,
  productSizeChart,
}) {
  const dispatch = useDispatch()
  const { LL } = useI18nContext()
  const [giftCardType, setGiftCardType] = useState("PHYSICAL")
  const [isPreorderAvailable, setIsPreorderAvailable] = useState(false)

  useEffect(() => {
    dispatch(updateNotifyStatus(pid))
  }, [pid])

  const getProductDetails = (params, amount, productConfigOptions, sku) => {
    const {
      pid,
      isQuickView,
      productDetails: { isProductConfigurable, isEngagementProduct },
    } = params

    const productDetails = {
      pid,
      sku,
      productConfigOptions,
      isQuickView,
      isProductConfigurable,
      isEngagementProduct,
      clearCart: true,
      amount,
    }

    return productDetails
  }

  /**
   * @description function to filter gift cards variations
   * @param {Object} Object containing all the variations
   * @param {String} String type of variation needed to be filtered
   * @returns {Object} Object containing required variations
   */
  const getVariations = (allVariations, variationType) => {
    const requiredVariations = allVariations.filter(
      (variant) => variant.giftCardType === variationType
    )
    return requiredVariations
  }

  /**
   * @description function to handle the selection/ deselection of checkbox
   * @param {Object} event of the element
   * @returns {void}
   */
  const selectBrailleCard = (event) => {
    const giftCardType = event.target.checked ? "BRAILLE" : "PHYSICAL"
    dispatch(
      setSelectedGiftCardAmount(
        pid,
        getVariations(variantOptions, giftCardType)[0]
      )
    )
    setGiftCardType(giftCardType)
  }

  function isSingleSizeSelected(selectedSize) {
    return selectedSize && selectedSize.size === ("ONE SIZE" || "NO SIZE")
  }

  const checkRopisStock = () => {
    let hasRopisStock = true
    if (selectedSize) {
      hasRopisStock =
        selectedSize.stock.stockLevel !== "0" ||
        (selectedSize.ropisStock && selectedSize.ropisStock.stockLevel !== "0")
    } else if (isSingleSizeSelected(selectedSize)) {
      hasRopisStock =
        priceInventory &&
        productDetails &&
        ((priceInventory.ropisStock &&
          priceInventory.ropisStock.stockLevel !== "0") ||
          priceInventory.stock.stockLevel !== "0" ||
          productDetails.stock.stockLevel !== "0")
    } else if (
      typeof priceInventory !== "undefined" &&
      Object.keys(priceInventory).length > 0
    ) {
      hasRopisStock =
        priceInventory &&
        ((priceInventory.ropisStock &&
          priceInventory.ropisStock.stockLevel !== "0") ||
          priceInventory.stock.stockLevel !== "0")
    } else if (
      typeof productDetails !== "undefined" &&
      Object.keys(productDetails).length > 0
    ) {
      hasRopisStock = productDetails.stock.stockLevel !== "0"
    }

    return hasRopisStock
  }

  const isAvailableForReserve = () => {
    const ropisStock = checkRopisStock()

    return (
      ropisStock &&
      !isGiftCard &&
      !isQuickView &&
      !isProductConfigurable &&
      !isEngagementProduct
    )
  }

  const checkIsPreorderAvailable = (priceInventory) => {
    const availabilityThreshold = getDY_SR().config.global.minimumQuantity

    if (
      priceInventory.stock.preOrderFlag === "true" &&
      +getValue(priceInventory, "stock.stockLevel") >= +availabilityThreshold &&
      !isPreorderAvailable
    ) {
      setIsPreorderAvailable(true)
    } else if (
      priceInventory.stock.preOrderFlag !== "true" &&
      isPreorderAvailable
    ) {
      setIsPreorderAvailable(false)
    }
  }

  const renderAddtoBagCTA = (showRopisCTA) => {
    const availabilityThreshold = getDY_SR().config.global.minimumQuantity

    let isNotAvailableForPurchase

    if (isSingleSizeSelected(selectedSize)) {
      isNotAvailableForPurchase =
        priceInventory &&
        +getValue(priceInventory, "stock.stockLevel") < +availabilityThreshold
      if (Object.keys(priceInventory).length !== 0)
        checkIsPreorderAvailable(priceInventory)
    } else if (
      selectedSize &&
      selectedSize.code &&
      selectedSize.code === priceInventory.productId
    ) {
      isNotAvailableForPurchase =
        priceInventory &&
        +getValue(priceInventory, "stock.stockLevel") < +availabilityThreshold
      checkIsPreorderAvailable(priceInventory)
    } else {
      isNotAvailableForPurchase =
        (selectedSize &&
          +getValue(selectedSize, "stock.stockLevel") <
            +availabilityThreshold) ||
        (priceInventory &&
          +getValue(priceInventory, "stock.stockLevel") <
            +availabilityThreshold)
      if (Object.keys(priceInventory).length !== 0)
        checkIsPreorderAvailable(priceInventory)
    }

    // based on Pinky Ring final step or normal PDP show the sizes or Add to Bag.
    // const showAddToBagCTAs = displayAddtoBag && !isEngagementProduct;
    // TODO: this is a WIP, getting the stackers to only ever sow "Add To Bag"
    const showAddToBagCTAs = !isEngagementProduct

    const showCustomerCareForm =
      isNotAvailableForPurchase && !isEngagementProduct && displayAddtoBag

    let selectedSku = isGiftCard
      ? selectedVariant && selectedVariant.code
      : selectedSize && selectedSize.code

    const varCode = selectedVariant.code
    const reLetters = /\D+/i
    if (refinerId) {
      if (varCode.indexOf(" ") > 0 || reLetters.test(varCode)) {
        if (selectedSize) {
          selectedSku = selectedSize.code
        } else {
          selectedSku = selectedVariant.variantOptions[0].code
        }
      } else {
        selectedSku = varCode
      }
    }

    const redirectUrl = shopTheLookURL || url

    const styleNumber = selectedSize
      ? selectedSize.styleNumber
      : selectedVariant.styleNumber
    const launchDate = selectedSize
      ? selectedSize.launchDate
      : selectedVariant.launchDate
    const scheduledShipDate =
      selectedSize && isPreorderAvailable
        ? selectedSize.stock.releaseDate
        : null
    const isInternationalCheckout = isInternationalCheckoutEnabled()
    const isNA = isNaSite()
    if (showAddToBagCTAs) {
      let productConfigOptions = {}
      if (design === PRConfig.DESIGN_VALUES[1]) {
        productConfigOptions = {
          motifCode,
        }
      } else if (design === PRConfig.DESIGN_VALUES[0]) {
        productConfigOptions = {
          monogramText: text,
          monogramFontcode: fontCode,
        }
      }
      let wishListProps = {
        sku: selectedVariant.code,
        url: redirectUrl,
      }
      if (enagraveableProductConfig) {
        wishListProps = {
          ...wishListProps,
          sku: selectedSize ? selectedSize.code : "",
          productConfigOptions,
          isProductConfigurable,
          pid,
        }
      }
      return (
        <Fragment>
          <div className="d-flex additional-ctas">
            <AddToBag
              enagraveableProductConfig={enagraveableProductConfig}
              errorMessage={message}
              isCartDataLoading={isDataLoading}
              isError={isError}
              isGiftCard={isGiftCard}
              isProductConfigurable={isProductConfigurable}
              isQuickView={isQuickView}
              isPreorder={isPreorderAvailable}
              scheduledShipDate={scheduledShipDate}
              pid={pid}
              productConfigOptions={productConfigOptions}
              productType={productType}
              sku={selectedSku}
            />
          </div>
          {/* QUICKVIEW LINK - Enable After Images are Provided*/}
          {/*{!isQuickView && (*/}
          {/*  <button*/}
          {/*    type="button"*/}
          {/*    className="btn btn-text btn-more-details"*/}
          {/*    onClick={onFullDetailsClick}*/}
          {/*  >*/}
          {/*    <strong>{LL.bracelets.moreDetails()}</strong>*/}
          {/*  </button>*/}
          {/*)}*/}
        </Fragment>
      )
    }
    return null
  }

  const onFullDetailsClick = useCallback(() => {
    dispatch(selectQuickViewProduct(pid, productDetails.url))
    dispatch(toggleModal(ModalConfig.MODAL_QUICK_VIEW, true))
  }, [dispatch, pid, productDetails.url])

  const isSwatchDropDown =
    variantOptions.length > SwatchConfig.SWATCH_PDP_CAROUSEL_LIMIT || false

  const hideSwatches =
    isEngagementProduct ||
    isProductConfigurable ||
    isGiftCard ||
    variantOptions.length === SwatchConfig.SWATCH_PDP_HIDE_LIMIT ||
    false

  const showPDPCTAs = enagraveableProductConfig || !isProductConfigurable

  const showSizes =
    !isEngagementProduct &&
    !isGiftCard &&
    showPDPCTAs &&
    selectedVariant.variantOptions &&
    selectedVariant.variantOptions.length > 0

  const isInternationalCheckout = isInternationalCheckoutEnabled()

  const showProductMetals =
    !isEngagementProduct &&
    productMetals &&
    productMetals.length > 1 &&
    !isProductConfigurable &&
    !enagraveableProductConfig

  const redirectUrl = shopTheLookURL || url
  const shouldUpdateDataLayer = isPDPPage && !isQuickView
  const isContactSpecialist = contactSpecialist === "true"
  const availableForReserve = !isPreorderAvailable && isAvailableForReserve()
  const preformattedDate =
    selectedSize &&
    priceInventory &&
    selectedSize.code === priceInventory.productId &&
    priceInventory.stock.preOrderFlag === "true" &&
    priceInventory.stock.releaseDate
  const preorderDate =
    selectedSize &&
    priceInventory &&
    selectedSize.code === priceInventory.productId &&
    priceInventory.stock.preOrderFlag === "true" &&
    formatPreorderDate(preformattedDate)

  return (
    <div className="ProductOverview">
      {isProductConfigurable && enagraveableProductConfig && (
        <div className="PinkyDetail">
          {text && design === PRConfig.DESIGN_VALUES[0] && (
            <div className="">
              <div className="detail-group row">
                <span className="detail-label col-3">Metal</span>
                <span className="detail-value col-9">{metal}</span>
              </div>
              <div className="detail-group row">
                <span className="detail-label col-3">Design</span>
                <span className="detail-value col-9">{design}</span>
              </div>
              <div className="detail-group row">
                <span className="detail-label col-3">Initials</span>
                <span className="detail-value col-9">{text}</span>
              </div>
            </div>
          )}
          {icon && design === PRConfig.DESIGN_VALUES[1] && (
            <div className="">
              <div className="detail-group row">
                <span className="detail-label col-3">Metal</span>
                <span className="detail-value col-9">{metal}</span>
              </div>
              <div className="detail-group row">
                <span className="detail-label col-3">Design</span>
                <span className="detail-value col-9">{design}</span>
              </div>
              <div className="detail-group row">
                <span className="detail-label col-3">Motif</span>
                <span className="detail-value col-9">
                  {icon.split("-").join(" ")}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {showProductMetals && (
        <ProductMetals
          isLink={!isQuickView}
          pid={pid}
          productMetals={productMetals}
          productName={selectedVariant.productName}
          refinerId={refinerId}
          selectedSize={selectedSize}
          shouldUpdateDataLayer={shouldUpdateDataLayer}
          shouldUpdateQuickViewLayer={isQuickView}
        />
      )}

      <ProductSwatches
        pid={pid}
        selectedVariantIndex={selectedVariantIndex}
        selectedVariant={selectedVariant}
        variantOptions={variantOptions}
        isSwatchDropDown={isSwatchDropDown}
        isAlphabetStone={isAlphabetStone}
        shouldUpdateDataLayer={shouldUpdateDataLayer}
        hideSwatches={hideSwatches}
        selectedSize={selectedSize}
        shouldUpdateQuickViewLayer={isQuickView}
        refinerId={refinerId}
      />

      {showSizes && !isContactSpecialist && (
        <ProductSizes
          isCsAgent={isCsAgent}
          isQuickView={isQuickView}
          isShowError={sizeSelectionError}
          pid={pid}
          productSizeChart={productSizeChart}
          productSizes={selectedVariant.variantOptions}
          refinerId={refinerId}
          selectedSize={selectedSize}
          shouldUpdateDataLayer={shouldUpdateDataLayer}
          shouldUpdateQuickViewLayer={isQuickView}
        />
      )}

      {isProductConfigurable && !enagraveableProductConfig && (
        <div className="pinky-ring-PDPtext">
          <p>{LL.productConfig.pinkyPDPText()}</p>
        </div>
      )}

      {renderAddtoBagCTA(availableForReserve)}

      {availableForReserve &&
        enableRopis &&
        !isContactSpecialist &&
        !isInternationalCheckout && (
          <div className="d-flex additional-ctas"></div>
        )}

      {(isEngagementProduct || isContactSpecialist) && purchasable && (
        <div className="wedding-ctas">
          <div className="d-flex additional-ctas"></div>
          <div className="d-flex"></div>
        </div>
      )}

      {isProductConfigurable && !enagraveableProductConfig && !isQuickView && (
        <div className="pinkyring-ctas">
          <CustomizeProductConfig
            configurableProductType={configurableProductType}
            pid={pid}
            productDetails={productDetails}
          />
        </div>
      )}

      {!isInternationalCheckout &&
        !isQuickView &&
        !isCsAgent &&
        !isEngagementProduct &&
        isPDPPage &&
        !isPreorderAvailable &&
        !isContactSpecialist && (
          <div id="loop-container" className="loop-container" />
        )}
    </div>
  )
}

ProductOverview.propTypes = {
  cartMetaData: PropTypes.object.isRequired,
  design: PropTypes.string,
  displayAddtoBag: PropTypes.bool,
  enableRopis: PropTypes.bool,
  enagraveableProductConfig: PropTypes.bool,
  fontCode: PropTypes.string,
  icon: PropTypes.string,
  isCsAgent: PropTypes.bool,
  isEPS: PropTypes.bool,
  isGiftCard: PropTypes.bool,
  isPDPPage: PropTypes.bool,
  isStackerPage: PropTypes.bool,
  isQuickView: PropTypes.bool,
  metal: PropTypes.string,
  motifCode: PropTypes.string,
  pid: PropTypes.string,
  priceInventory: PropTypes.object,
  productDetails: PropTypes.object.isRequired,
  productMetals: PropTypes.array,
  productSizeChart: PropTypes.object,
  productType: PropTypes.string,
  refinerId: PropTypes.string,
  selectedSize: PropTypes.object,
  selectedVariant: PropTypes.object.isRequired,
  selectedVariantIndex: PropTypes.number.isRequired,
  shopTheLookURL: PropTypes.string,
  sizeSelectionError: PropTypes.bool,
  text: PropTypes.string,
}

export default ProductOverview
